import { Box, Tab, Typography } from "@mui/material";
import { useCallback, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { IRootState } from "src/redux/reducers/CombinedReducers";
import { setIsAircraftContextOpen, setAircraftContextId } from "src/redux/actions/flights/FlightTimeline.Actions";
import ContextMenu from "../ContextMenu/ContextMenu";
import { OcTabs } from "../OcTabs/OcTabs";
import { AircraftOverviewTab } from "./AircraftOverviewTab";
import { AircraftMaintenanceTab } from "./AircraftMaintenanceTab";
import { IAircraft } from "@vmosolutions/oc-interfaces";
import FlightIcon from "@mui/icons-material/Flight";

interface IAircraftContextMenuProps {
  windowId: string;
}

interface ITabConfig {
  key: string;
  label: string;
  disabled: boolean;
  content: (aircraft: IAircraft | null, isMaintenanceEnabled: boolean) => JSX.Element;
}

const TABS: ITabConfig[] = [
  {
    key: "overview",
    label: "Overview",
    disabled: false,
    content: (aircraft: IAircraft | null) => <AircraftOverviewTab aircraft={aircraft} />,
  },
  {
    key: "maintenance",
    label: "Maintenance",
    disabled: false,
    content: (aircraft: IAircraft | null, isMaintenanceEnabled: boolean) => (
      <AircraftMaintenanceTab aircraft={aircraft} maintenanceEnabled={isMaintenanceEnabled} />
    ),
  },
  {
    key: "opslink",
    label: "OpsLink",
    disabled: true,
    content: () => (
      <Box sx={{ p: 2 }}>
        <Typography variant="body2" color="text.secondary">
          OpsLink integration is not yet available.
        </Typography>
      </Box>
    ),
  },
];

export const AircraftContextMenu = ({ windowId }: IAircraftContextMenuProps): JSX.Element => {
  const dispatch = useDispatch();
  const [activeTab, setActiveTab] = useState<number>(0);

  // Redux state
  const isAircraftContextOpenMap = useSelector((state: IRootState) => state.timelineFlights.isAircraftContextOpen);
  const aircraftContextIdMap = useSelector((state: IRootState) => state.timelineFlights.aircraftContextId);
  const aircraftContextInitialTabMap = useSelector(
    (state: IRootState) => state.timelineFlights.aircraftContextInitialTab
  );
  const allAircraft: IAircraft[] = useSelector((state: IRootState) => state.structures.customerStructure.aircraft);

  const isMaintenanceEnabled = useMemo(() => {
    return allAircraft?.some((ac) => ac.maintenance != null) ?? false;
  }, [allAircraft]);

  const isOpen = useMemo(() => {
    return isAircraftContextOpenMap?.[windowId] ?? false;
  }, [isAircraftContextOpenMap, windowId]);

  const selectedAircraftId = useMemo(() => {
    return aircraftContextIdMap?.[windowId] ?? null;
  }, [aircraftContextIdMap, windowId]);

  // When the panel freshly opens or the selected aircraft changes,
  // set the active tab to the requested initial tab from Redux.
  const [prevIsOpen, setPrevIsOpen] = useState(false);
  const [prevAircraftId, setPrevAircraftId] = useState<string | null>(null);

  if (isOpen !== prevIsOpen || selectedAircraftId !== prevAircraftId) {
    const isFreshOpen = (isOpen && !prevIsOpen) || (isOpen && selectedAircraftId !== prevAircraftId);

    setPrevIsOpen(isOpen);
    setPrevAircraftId(selectedAircraftId);

    if (isFreshOpen) {
      const initialTabKey = aircraftContextInitialTabMap?.[windowId] ?? "overview";
      const tabIndex = TABS.findIndex((t) => t.key === initialTabKey);
      setActiveTab(tabIndex >= 0 ? tabIndex : 0);
    }
  }

  const selectedAircraft = useMemo(() => {
    if (!selectedAircraftId || !allAircraft) return null;
    return allAircraft.find((ac: IAircraft) => ac._id === selectedAircraftId) ?? null;
  }, [selectedAircraftId, allAircraft]);

  const handleClose = useCallback((): void => {
    dispatch(setIsAircraftContextOpen(false, windowId));
    dispatch(setAircraftContextId(null, windowId));
    setActiveTab(0);
  }, [dispatch, windowId]);

  const handleTabChange = useCallback((_event: React.SyntheticEvent, newValue: number): void => {
    if (TABS[newValue]?.disabled) return;
    setActiveTab(newValue);
  }, []);

  // Automatically switch tabs to vertical when there are many tabs.
  // With 3 tabs horizontal works fine. When more are added (e.g. Flight Logs, History),
  // this threshold will trigger vertical orientation.
  const tabOrientation: "horizontal" | "vertical" = useMemo(() => {
    return TABS.length > 4 ? "vertical" : "horizontal";
  }, []);

  const menuHeader = useMemo(() => {
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <FlightIcon sx={{ fontSize: 20 }} />
        <Typography variant="h6" sx={{ fontSize: "1.1rem", fontWeight: 600 }}>
          {selectedAircraft ? selectedAircraft.registration : "Aircraft Details"}
        </Typography>
      </Box>
    );
  }, [selectedAircraft]);

  const panelWidth = "clamp(320px, 30vw, 450px)";

  return (
    <ContextMenu
      id={`${windowId}-aircraftContextMenu`}
      isOpen={isOpen}
      key={`${windowId}-aircraft-contextmenu`}
      maxWidth={panelWidth}
      onClose={handleClose}
      menuHeader={menuHeader}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: tabOrientation === "vertical" ? "row" : "column",
          height: "calc(100% - 64px)",
        }}
      >
        {/* Tabs */}
        <OcTabs
          value={activeTab}
          onChange={handleTabChange}
          orientation={tabOrientation}
          variant="scrollable"
          scrollButtons="auto"
        >
          {TABS.map((tab) => (
            <Tab
              key={tab.key}
              label={tab.disabled ? `${tab.label} (disabled)` : tab.label}
              disabled={tab.disabled}
              sx={tab.disabled ? { opacity: 0.4, cursor: "not-allowed" } : undefined}
            />
          ))}
        </OcTabs>

        {/* Tab Content */}
        <Box sx={{ flexGrow: 1, overflow: "auto" }}>
          {TABS[activeTab]?.content(selectedAircraft, isMaintenanceEnabled)}
        </Box>
      </Box>
    </ContextMenu>
  );
};
